import { FC, useMemo } from 'react';
import {
  Heading,
  Pagination,
  Table,
  isMobileQuery,
  usePagination,
} from '@higo/ui';
import styled from 'styled-components';
import { usePatientExaminationListQuery } from 'components/PatientExaminationsTable/usePatientExaminationListQuery';
import { usePatientHistoryQuery } from 'components/PatientExaminationsTable/usePatientHistoryQuery';
import {
  mapToPatientExamsList,
  mapToPatientHistoryList,
  PatientExamsListItem,
  usePatientExamsConfig,
  usePatientExamsConfigForMobile,
} from 'components/PatientExaminationsTable/config';
import { useNavigate } from 'react-router-dom';
import { getExaminationRoute, getVisitRoute } from 'routes';
import { FormattedMessage } from 'react-intl';
import { useMedia } from 'react-use';

const StyledHeading = styled(Heading)`
  margin-left: 16px;
`;

export type PatientExaminationsTableProps = {
  patientId: number;
  onRowNavigate?: () => void;
};

export const PatientExaminationsTable: FC<PatientExaminationsTableProps> = ({
  patientId,
  onRowNavigate,
}) => {
  const navigate = useNavigate();
  const config = usePatientExamsConfig();
  const configForMobile = usePatientExamsConfigForMobile();
  const isMobileView = useMedia(`${isMobileQuery}`);

  const pagination = usePagination();
  const isVisitsAvailable = process.env.VISIT_AVAILABLE === 'true';
  const pageData = useMemo(
    () => ({ page: pagination.page, pageSize: pagination.pageSize }),
    [pagination.page, pagination.pageSize],
  );

  const { data: examsData, isLoading: isLoadingExams, isError: isErrorExams } = usePatientExaminationListQuery(
    patientId,
    pageData,
    { enabled: !isVisitsAvailable },
  );

  const { data: historyData, isLoading: isLoadingHistory, isError: isErrorHistory } = usePatientHistoryQuery(
    patientId,
    pageData,
    { enabled: isVisitsAvailable },
  );

  const tableData: PatientExamsListItem[] = useMemo(() => {
    if (isVisitsAvailable && historyData) {
      return mapToPatientHistoryList(
        historyData.examinations ?? [],
        historyData.visits ?? [],
      );
    }
    return mapToPatientExamsList(examsData?.content ?? []);
  }, [isVisitsAvailable, historyData, examsData]);

  const isFetching = isVisitsAvailable ? isLoadingHistory : isLoadingExams;
  const isError = isVisitsAvailable ? isErrorHistory : isErrorExams;
  const totalPages = isVisitsAvailable ? 1 : (examsData?.totalPages ?? 0);

  const handleRowClick = (row: { original: PatientExamsListItem }) => {
    const id = row.original.id;
    if (typeof id !== 'number' || isNaN(id)) {
      return;
    }

    onRowNavigate?.();

    if (row.original.isVisit) {
      navigate(getVisitRoute(id));
    } else {
      navigate(getExaminationRoute(id));
    }
  };

  return (
    <>
      <StyledHeading level={2}>
        <div id="history-modal">
          <FormattedMessage id="patientHistoryModal.title" />
        </div>
      </StyledHeading>
      {!isMobileView ? (
        <Table
          {...config}
          data={tableData}
          pageSize={pagination.pageSize}
          isLoading={isFetching || isError}
          onRowClick={handleRowClick}
        />
      ) : (
        <Table
          {...configForMobile}
          data={tableData}
          pageSize={pagination.pageSize}
          isLoading={isFetching || isError}
          onRowClick={handleRowClick}
        />
      )}
      <Pagination
        {...pagination}
        totalPages={totalPages}
        currentPageLabelSlot={
          <FormattedMessage
            id="pagination.pageCurrentOfTotal"
            values={{
              current: pagination.page,
              total: totalPages,
            }}
          />
        }
        perPageLabel={<FormattedMessage id="pagination.examsPerPage" />}
      />
    </>
  );
};
